import { memo, useEffect, useMemo, useState } from "react";
import type { InitOptions, RenderRouteType } from "@qubictron/maplib";
import { Map as MapLib, MapEngine } from "@qubictron/maplib";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { MapClusterColorsMap } from "shared/api/lib/map.types";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { MAP_STYLE_URL, mapThemeStore } from "features/ThemeSwitcher";
import { appStore } from "entities/App";
import { panelStore } from "entities/Panel";
import { searchFilterStore } from "entities/SearchFilter";
import { ShipmentTypeChecker } from "entities/shared/utils";
import { useMobile } from "shared/hooks/useMobile.hook";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { Error } from "./components/Error";
import "@qubictron/maplib/styles.css";
import "./Map.module.scss";

import { useAirMapContent } from "../model/useAirMapContent";
import { useParcelMapContent } from "../model/useParcelMapContent";
import { useRailMapContent } from "../model/useRailMapContent";
import { useRoadMapContent } from "../model/useRoadMapContent";
import { useSeaMapContent } from "../model/useSeaMapContent";
import { MapErrorBoundary } from "./components/MapErrorBoundary.container";

const PANEL_WIDTH = 608;
/** Zoom control stack height (two 28px buttons + 4px gap) plus the gap to the next control above it. */
const CONTROL_STACK_OFFSET = 68;

const MAP_CONFIG_BASE: Omit<InitOptions, "style"> = {
  center: [0, 0],
  zoom: 1,
};

export const MapComponent = memo(() => {
  const { isMobile } = useMobile({ debounceTime: 60 });
  const currentView = useAppSelector(panelStore.selectors.currentView);
  const { isCurrentViewOpenedCard } = usePanelViews();
  const dispatch = useAppDispatch();
  const shipment = useAppSelector(searchFilterStore.selectors.shipment);
  const mapTheme = useAppSelector(mapThemeStore.selectors.style);
  const [mapInstance, setMapInstance] = useState<MapEngine | null>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  const mapStyleUrl = MAP_STYLE_URL[mapTheme];

  const showPanel = () => {
    dispatch(appStore.actions.setIsPanelVisible(true));
  };

  const {
    isSeaRouteError,
    points: seaPoints,
    renderRoute: seaRenderRoute,
    renderRoutePoints: seaRenderRoutePoints,
  } = useSeaMapContent({
    isMobile,
    showPanel,
    isMapLoaded,
    isDisabled: !ShipmentTypeChecker.isSea(shipment),
  });
  const {
    isAirRouteError,
    points: airPoints,
    renderRoute: airRenderRoute,
    renderRoutePoints: airRenderRoutePoints,
  } = useAirMapContent({
    isMobile,
    showPanel,
    isMapLoaded,
    isDisabled: !ShipmentTypeChecker.isAir(shipment),
  });
  const {
    isRailRouteError,
    points: railPoints,
    renderRoute: railRenderRoute,
    renderRoutePoints: railRenderRoutePoints,
  } = useRailMapContent({
    isMobile,
    showPanel,
    isMapLoaded,
    isDisabled: !ShipmentTypeChecker.isRail(shipment),
  });
  const {
    isRoadRouteError,
    points: roadPoints,
    renderRoute: roadRenderRoute,
    renderRoutePoints: roadRenderRoutePoints,
  } = useRoadMapContent({
    isMobile,
    showPanel,
    isMapLoaded,
    isDisabled: !ShipmentTypeChecker.isRoad(shipment),
  });
  const {
    isParcelRouteError,
    points: parcelPoints,
    renderRoute: parcelRenderRoute,
    renderRoutePoints: parcelRenderRoutePoints,
  } = useParcelMapContent({
    isMobile,
    showPanel,
    isMapLoaded,
    isDisabled: !ShipmentTypeChecker.isParcel(shipment),
  });

  const isRouteError = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return isSeaRouteError;
      case ShipmentType.AIR:
        return isAirRouteError;
      case ShipmentType.RAIL:
        return isRailRouteError;
      case ShipmentType.ROAD:
        return isRoadRouteError;
      case ShipmentType.PARCEL:
        return isParcelRouteError;
      default:
        return false;
    }
  }, [isSeaRouteError, isAirRouteError, isRailRouteError, isRoadRouteError, isParcelRouteError, shipment]);

  const points = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return seaPoints;
      case ShipmentType.AIR:
        return airPoints;
      case ShipmentType.RAIL:
        return railPoints;
      case ShipmentType.ROAD:
        return roadPoints;
      case ShipmentType.PARCEL:
        return parcelPoints;
      default:
        return [];
    }
  }, [shipment, seaPoints, airPoints, railPoints, roadPoints, parcelPoints]);

  const routeRender: RenderRouteType = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return seaRenderRoute;
      case ShipmentType.AIR:
        return airRenderRoute;
      case ShipmentType.RAIL:
        return railRenderRoute;
      case ShipmentType.ROAD:
        return roadRenderRoute;
      case ShipmentType.PARCEL:
        return parcelRenderRoute;
      default:
        return { route: [] };
    }
  }, [shipment, seaRenderRoute, airRenderRoute, railRenderRoute, roadRenderRoute, parcelRenderRoute]);

  const routeFitKey = useMemo(() => {
    const firstSegment = routeRender.route[0];
    const lastSegment = routeRender.route[routeRender.route.length - 1];
    const first = firstSegment?.coordinates[0];
    const lastCoords = lastSegment?.coordinates;
    const last = lastCoords?.[lastCoords.length - 1];
    const pin = routeRender.currentPin;
    return [routeRender.route.length, first?.lng, first?.lat, last?.lng, last?.lat, pin?.lng, pin?.lat].join(":");
  }, [routeRender]);

  const renderRoutePoints = useMemo(() => {
    switch (shipment) {
      case ShipmentType.SEA:
        return seaRenderRoutePoints;
      case ShipmentType.AIR:
        return airRenderRoutePoints;
      case ShipmentType.RAIL:
        return railRenderRoutePoints;
      case ShipmentType.ROAD:
        return roadRenderRoutePoints;
      case ShipmentType.PARCEL:
        return parcelRenderRoutePoints;
      default:
        return [];
    }
  }, [
    shipment,
    seaRenderRoutePoints,
    airRenderRoutePoints,
    railRenderRoutePoints,
    roadRenderRoutePoints,
    parcelRenderRoutePoints,
  ]);

  useEffect(() => {
    if (!isMapLoaded) return;

    mapInstance?.unmountRoute();
    mapInstance?.unmountPoints();
    mapInstance?.flyToOrigin();
  }, [shipment]);

  useEffect(() => {
    // maplibre throws "Style is not done loading." from addSource if anything
    // renders before the style is ready, and this effect runs in the component
    // that owns the boundary, so that throw took the whole widget down with it.
    if (!isMapLoaded) return;

    if (isCurrentViewOpenedCard) {
      mapInstance?.unmountPoints();
      if (routeRender.route.length) {
        mapInstance?.renderRoute(routeRender);
        mapInstance?.renderPoints(renderRoutePoints, {
          mergeNearby: false,
        });
        mapInstance?.flyToRoute();
      }
    }
    if (!isCurrentViewOpenedCard) {
      if (routeRender.route.length) {
        mapInstance?.flyToOrigin();
        mapInstance?.unmountRoute();
      }
      if (points.length) {
        mapInstance?.renderPoints(points, {
          cluster: {
            circleColor: MapClusterColorsMap[shipment],
          },
          mergeNearby: 10,
        });
      }
    }
  }, [
    currentView,
    isCurrentViewOpenedCard,
    routeFitKey,
    mapInstance,
    points,
    shipment,
    isMapLoaded,
    routeRender,
    renderRoutePoints,
  ]);

  const zoomControlBottom = isMobile ? 75 : isCurrentViewOpenedCard ? 24 : 24;

  return (
    <MapErrorBoundary>
      {isRouteError && <Error />}

      <MapLib
        getMap={(engine) => {
          setMapInstance(engine);
          // getMap fires the moment the engine is constructed, which is well
          // before the style has loaded - wait for the map to say so itself.
          if (engine.map.isStyleLoaded()) {
            setIsMapLoaded(true);
            return;
          }
          engine.map.once("load", () => setIsMapLoaded(true));
        }}
        initOptions={{
          ...MAP_CONFIG_BASE,
          style: mapStyleUrl,
          fitBoundsPadding: {
            top: 50,
            left: isMobile ? 50 : PANEL_WIDTH + 50,
            right: 50,
            bottom: 50,
          },
          zoomControl: {
            bottom: zoomControlBottom,
          },
          globeControl: {
            bottom: zoomControlBottom + CONTROL_STACK_OFFSET,
          },
          theme: mapTheme,
        }}
      />
    </MapErrorBoundary>
  );
});

MapComponent.displayName = "MapComponent";
